import { DepressionContentPage } from "@/components/DepressionContentPage";

const DepressionTreatmentFaq = () => (
  <DepressionContentPage
    seo={{
      title:
        "Depression Treatment FAQ — TMS, SAINT®, SPRAVATO® Questions | Neuro Wellness",
      description:
        "Answers to common questions about advanced depression treatment — TMS, SAINT®, SPRAVATO®, medication, cost, insurance, and how to get started.",
      canonical: "/depression-treatment-faq",
      keywords:
        "depression treatment FAQ, TMS questions, SAINT questions, SPRAVATO questions, depression treatment cost FAQ",
    }}
    badge="Frequently Asked Questions"
    heroTitle="Questions About Advanced Depression Treatment"
    heroSubtitle="You're not the only one with these questions. Here are answers to the things people ask us most often — in plain language, without pressure."
    sections={[
      {
        heading: "Getting Started",
        variant: "text",
        body: (
          <p>
            Starting the conversation is often the hardest part. Here's what you
            need to know before you reach out.
          </p>
        ),
      },
    ]}
    faq={[
      {
        question:
          "How do I know when it's time to consider another depression treatment?",
        answer:
          "If you've tried medication and/or therapy and are still experiencing depression, it may be worth learning about other options. The decision to explore additional treatments is made with your clinician based on your full history.",
      },
      {
        question: "Do I need a referral?",
        answer:
          "You can contact us directly to schedule a consultation. If you have a current psychiatrist or therapist, we can coordinate with them, but a formal referral is not required to start the conversation.",
      },
      {
        question: "Do I need a diagnosis?",
        answer:
          "A consultation helps us understand your situation. If you have an existing diagnosis, that's helpful. If not, we can discuss next steps during the consultation.",
      },
      {
        question: "Can a family member call for me?",
        answer:
          "Yes. A family member may contact us to learn general information about our services and the consultation process. The patient must consent to and participate in treatment.",
      },
      {
        question: "Can I call for my adult child?",
        answer:
          "Yes. You can call to learn about treatment options on behalf of your adult child. The patient themselves must consent to treatment, but gathering information is something you can do now.",
      },
      {
        question: "Can I speak with someone before deciding on a treatment?",
        answer:
          "Absolutely. That's exactly what the consultation is for. You don't need to know which treatment you want before calling us.",
      },
      {
        question: "Can I receive TMS while taking antidepressants?",
        answer:
          "TMS may be used alongside oral antidepressants. Whether that's appropriate for you is a clinical decision made with your care team. Do not stop or change medication without discussing it with your prescribing provider.",
      },
      {
        question: "What if I'm starting a new antidepressant?",
        answer:
          "If you're starting a new medication, it's often worth allowing adequate time to assess its effect before exploring other options. Your prescriber can help you think through the timing.",
      },
      {
        question: "Should I stop medication before TMS?",
        answer:
          "Do not stop or change prescribed medication without discussing it with your prescribing healthcare provider. TMS does not require you to stop medication.",
      },
      {
        question: "What if medication hasn't worked?",
        answer:
          "If medication hasn't provided adequate relief, you may be experiencing treatment-resistant depression. Other treatments, including TMS, SAINT®, and SPRAVATO®, work through different mechanisms and may be worth exploring.",
      },
      {
        question: "What does TMS feel like?",
        answer:
          "Most people describe a tapping sensation on the scalp during treatment. Some experience mild scalp discomfort or headache, which typically improves over the course of treatment.",
      },
      {
        question: "Is TMS painful?",
        answer:
          "TMS is generally not described as painful. Some people experience mild discomfort at the treatment site, particularly during early sessions. This usually decreases over time.",
      },
      {
        question: "Is anesthesia required for TMS?",
        answer:
          "No. TMS is a non-invasive treatment performed while you are awake. No anesthesia or sedation is required for standard TMS.",
      },
      {
        question: "Can I drive after TMS?",
        answer:
          "For standard TMS, most people can drive themselves to and from appointments. SAINT® and other accelerated protocols may have different guidance — ask your care team.",
      },
      {
        question: "How long is TMS treatment?",
        answer:
          "A traditional TMS course typically involves sessions over several weeks. Accelerated protocols, like SAINT®, compress treatment into five consecutive days.",
      },
      {
        question: "How many TMS sessions are involved?",
        answer:
          "A traditional course typically involves multiple sessions per week over several weeks. SAINT® delivers multiple sessions per day over five days. The exact number is determined clinically.",
      },
      {
        question: "Is TMS a medication?",
        answer:
          "No. TMS is a non-invasive treatment that uses magnetic stimulation rather than medication to stimulate targeted areas of the brain associated with mood regulation.",
      },
      {
        question: "How does TMS work?",
        answer:
          "TMS uses magnetic pulses to stimulate specific areas of the brain involved in mood regulation. It is believed to influence brain activity in regions associated with depression.",
      },
      {
        question: "What is SAINT®?",
        answer:
          "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is an FDA-cleared accelerated TMS protocol that uses fMRI to individually map and target the treatment area.",
      },
      {
        question: "How is SAINT® different from traditional TMS?",
        answer:
          "SAINT® differs in its use of fMRI-guided individualized targeting and its accelerated five-day schedule. Traditional TMS uses a standard targeting approach over several weeks.",
      },
      {
        question: "Why is SAINT® considered accelerated TMS?",
        answer:
          "SAINT® delivers multiple treatment sessions per day over five consecutive days, compressing what is traditionally a multi-week course into a single week.",
      },
      {
        question: "How is the SAINT® treatment target selected?",
        answer:
          "SAINT® uses an fMRI scan to identify the treatment target specific to your brain's connectivity, rather than relying on population averages.",
      },
      {
        question: "How long does the SAINT® treatment protocol take?",
        answer:
          "The SAINT® protocol takes place over five consecutive days, with multiple sessions per day.",
      },
      {
        question: "Who may qualify for SAINT®?",
        answer:
          "Eligibility for SAINT® is determined through clinical evaluation. It is intended for adults with treatment-resistant depression. Whether it's appropriate for you is a clinical decision.",
      },
      {
        question: "What is SPRAVATO®?",
        answer:
          "SPRAVATO® (esketamine) is an FDA-approved prescription nasal spray for treatment-resistant depression, administered under medical supervision at a certified treatment center.",
      },
      {
        question: "Is SPRAVATO® the same as ketamine?",
        answer:
          "SPRAVATO® contains esketamine, a form of ketamine. However, they are different treatments with different administration, regulation, and insurance considerations. SPRAVATO® is FDA-approved and given as a nasal spray; IV ketamine is administered as an infusion and used off-label for depression.",
      },
      {
        question: "How is SPRAVATO® administered?",
        answer:
          "SPRAVATO® is a nasal spray that you self-administer under the supervision of a healthcare provider at a certified treatment center. You remain for observation afterward.",
      },
      {
        question:
          "Why does SPRAVATO® have to be administered in a medical setting?",
        answer:
          "Because of its risks, SPRAVATO® is only available through a restricted program called the SPRAVATO® REMS. It must be given in a certified setting with monitoring afterward.",
      },
      {
        question: "How long will I be at the office for SPRAVATO®?",
        answer:
          "Plan for about two to three hours per visit, including the required monitoring period after each dose.",
      },
      {
        question: "Will I need someone to drive me for SPRAVATO®?",
        answer:
          "Yes. You cannot drive after a SPRAVATO® session. You'll need to arrange a ride home after each visit.",
      },
      {
        question: "Does insurance cover TMS?",
        answer:
          "Many plans cover TMS for treatment-resistant depression when medical necessity criteria are met. Coverage varies by plan. We verify your benefits before any financial commitment.",
      },
      {
        question: "Does insurance cover SAINT®?",
        answer:
          "Coverage for SAINT® varies by plan. We verify your benefits and handle prior authorization on your behalf.",
      },
      {
        question: "Does insurance cover SPRAVATO®?",
        answer:
          "Many commercial plans and Medicare cover SPRAVATO® with prior authorization. We verify your specific plan's coverage.",
      },
      {
        question: "What if I'm paying privately?",
        answer:
          "We offer self-pay options. Contact our team to discuss pricing and payment arrangements that may work for your situation.",
      },
      {
        question: "Can you verify my insurance before treatment?",
        answer:
          "Yes. We verify your benefits and handle prior authorization before treatment begins, so you know your cost before you commit.",
      },
    ]}
    relatedLinks={[
      {
        title: "Depression Treatment Options",
        href: "/depression-treatment-options",
      },
      {
        title: "What Happens During a Consultation",
        href: "/depression-treatment-consultation",
      },
      {
        title: "Depression Treatment Cost",
        href: "/depression-treatment-cost",
      },
      {
        title: "Insurance for TMS & Depression Treatment",
        href: "/insurance-for-tms-depression-treatment",
      },
    ]}
    finalCtaHeading="Still Have Questions?"
    finalCtaText="We're happy to answer them. Call our team for a free consultation — no pressure, no commitment, just answers."
  />
);

export default DepressionTreatmentFaq;
